// src/pages/calendar/bookings/BookingsTable.js - Using ReusableTable component
import React, { useMemo } from "react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Card as MuiCard,
  CardContent,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Videocam as VideocamIcon,
  OpenInNew as OpenInNewIcon,
  Summarize as SummarizeIcon,
  Delete as DeleteIcon,
  LinkOff as LinkOffIcon,
} from "@mui/icons-material";

// Import ReusableTable component
import ReusableTable from "../../../components/Common/ReusableTable";

import {
  getBookingStatus,
  formatDateTimeDisplay,
  getStatusColor,
} from "./utils/bookingUtils";

// Booking Card Component (for ReusableTable card view)
const Card = styled(MuiCard)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

// Booking Card Component for ReusableTable
const BookingCard = ({
  data: booking,
  selected: isSelected,
  onSelect,
  index,
}) => {
  // Format date and time for display
  const { dateDisplay, timeDisplay } = formatDateTimeDisplay(
    booking.date,
    booking.timeStart,
    booking.timeEnd,
    booking.timezone
  );

  // Get event format
  const eventFormat = booking.eventFormat || "meeting";

  // Check if we have meeting info from the backend
  const hasMeetingInfo =
    booking && booking.meetingInfo && booking.meetingInfo.link;
  const hasEventTypeId = booking && booking.eventTypeId;

  return (
    <Card>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box position="relative">
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelect(e, booking.id)}
            sx={{ position: "absolute", top: -8, right: -8 }}
          />

          <Box mb={2}>
            <Typography variant="h6" gutterBottom>
              {booking.eventTitle || "Loading..."}
            </Typography>

            <Chip
              size="small"
              color={getStatusColor(booking.status)}
              label={
                booking.status
                  ? booking.status.charAt(0).toUpperCase() +
                    booking.status.slice(1)
                  : "Unknown"
              }
              variant="outlined"
              sx={{ mb: 1 }}
            />

            <Typography variant="body2" fontWeight="500" gutterBottom>
              {dateDisplay}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {timeDisplay}
            </Typography>
          </Box>

          {/* Booker Info */}
          <Box mb={2}>
            <Typography variant="body1" fontWeight="500">
              {booking.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {booking.email}
            </Typography>

            {/* Show event format badge */}
            <Chip
              label={formatEventType(eventFormat)}
              size="small"
              variant="outlined"
              color="info"
              sx={{ mt: 0.5, mb: 0.5, fontSize: "0.675rem" }}
            />

            {/* Meeting Link */}
            {hasMeetingInfo && booking.meetingInfo.status !== "revoked" && booking.meetingInfo.status !== "expired" && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  href={booking.meetingInfo.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<VideocamIcon fontSize="small" />}
                  sx={{ fontSize: "0.75rem", py: 0.5 }}
                >
                  Join Meeting
                </Button>
                {onEndMeeting && booking.meetingRecordId && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => onEndMeeting(booking)}
                    sx={{ fontSize: "0.75rem", py: 0.5 }}
                  >
                    End Meeting
                  </Button>
                )}
              </Stack>
            )}
            {booking.meetingInfo?.status === "revoked" && (
              <Chip label="Link Revoked" color="error" size="small" sx={{ mt: 1 }} />
            )}
            {booking.meetingInfo?.status === "expired" && (
              <Chip label="Link Expired" color="warning" size="small" sx={{ mt: 1 }} />
            )}

            {/* Fallback Event Link */}
            {!hasMeetingInfo && hasEventTypeId && (
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                href={`/preview/preview-event-type?eventTypeId=${booking.eventTypeId}`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ mt: 1, fontSize: "0.75rem", py: 0.5 }}
              >
                View Event
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Helper function to format event type
const formatEventType = (eventFormat) => {
  console.log("event ytpe !!!@@@@:", eventFormat);
  if (!eventFormat) return "Meeting";

  // Convert from kebab-case or snake_case to Title Case
  return eventFormat
    .replace(/[-_]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Booking table component using ReusableTable
function BookingsTable({
  loading,
  bookings,
  eventTypesData,
  selected,
  setSelected,
  loadingActions,
  viewStyle,
  setViewStyle,
  handleCancelBooking,
  handleRescheduleBooking,
  handleRequestReschedule,
  handleDeleteBooking,
  handleAcceptReject,
  setSelectedBookingId,
  setCancelDialog,
  setRescheduleDialog,
  setRequestRescheduleDialog,
  setDeleteDialog,
  setRescheduleDate,
  setRescheduleTime,
  setRescheduleTimezone,
  onMeetingSummaryClick,
  onEndMeeting,
  setKillLinkDialog,
  // Pagination props
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  order,
  setOrder,
  orderBy,
  setOrderBy,
}) {
  // Selection handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = bookings.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Process bookings data for ReusableTable
  const processedBookings = useMemo(() => {
    return bookings.map((booking) => {
      // Format date and time for display
      const { dateDisplay, timeDisplay } = formatDateTimeDisplay(
        booking.date,
        booking.timeStart,
        booking.timeEnd,
        booking.timezone
      );

      // Get event type data if available
      const eventType = eventTypesData[booking.eventTypeId] || {};

      // Determine if booking requires confirmation
      const requiresConfirmation =
        eventType?.advanced?.requiresConfirmation?.enabled || false;

      // Determine booking status
      const status = booking.status || getBookingStatus(booking, eventType);

      // Get event format if available
      const eventFormat = eventType?.classification?.eventFormat || "meeting";

      return {
        ...booking,
        formattedDate: dateDisplay,
        formattedTime: timeDisplay,
        requiresConfirmation,
        status,
        eventTitle: eventType?.title || "Loading...",
        eventFormat,
      };
    });
  }, [bookings, eventTypesData]);

  // Define columns for ReusableTable
  const columns = useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
      },
      {
        field: "datetime",
        headerName: "Date & Time",
        width: 200,
        renderCell: (params) => (
          <Box>
            <Typography variant="body2" fontWeight="500">
              {params.row.formattedDate}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {params.row.formattedTime}
            </Typography>
          </Box>
        ),
      },
      {
        field: "eventTitle",
        headerName: "Event Type",
        width: 200,
      },
      {
        field: "booker",
        headerName: "Booker",
        flex: 1,
        renderCell: (params) => {
          const booking = params.row;
          const hasMeetingInfo =
            booking && booking.meetingInfo && booking.meetingInfo.link;
          const hasEventTypeId = booking && booking.eventTypeId;
          const eventFormat = booking.eventFormat || "meeting";

          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {booking.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {booking.email}
              </Typography>

              <Chip
                label={formatEventType(eventFormat)}
                size="small"
                variant="outlined"
                color="info"
                sx={{ mt: 0.5, mb: 0.5, fontSize: "0.675rem" }}
              />

              {hasMeetingInfo && booking.meetingInfo.status !== "revoked" && booking.meetingInfo.status !== "expired" && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    href={booking.meetingInfo.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<VideocamIcon fontSize="small" />}
                    sx={{ fontSize: "0.75rem", py: 0.5 }}
                  >
                    Join Meeting
                  </Button>
                  {onEndMeeting && booking.meetingRecordId && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => onEndMeeting(booking)}
                      sx={{ fontSize: "0.75rem", py: 0.5 }}
                    >
                      End Meeting
                    </Button>
                  )}
                </Stack>
              )}
              {booking.meetingInfo?.status === "revoked" && (
                <Chip label="Link Revoked" color="error" size="small" sx={{ mt: 1 }} />
              )}
              {booking.meetingInfo?.status === "expired" && (
                <Chip label="Link Expired" color="warning" size="small" sx={{ mt: 1 }} />
              )}

              {!hasMeetingInfo && hasEventTypeId && (
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  href={`/preview/preview-event-type?eventTypeId=${booking.eventTypeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  sx={{ mt: 1, fontSize: "0.75rem", py: 0.5 }}
                >
                  View Event
                </Button>
              )}
            </Box>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (params) => {
          const status = params.row.status || "unknown";
          let label = "";

          switch (status) {
            case "upcoming":
              label = "Upcoming";
              break;
            case "confirmed":
              label = "Confirmed";
              break;
            case "past":
              label = "Past";
              break;
            case "pending":
              label = "Unconfirmed";
              break;
            case "canceled":
              label = "Canceled";
              break;
            case "rejected":
              label = "Rejected";
              break;
            default:
              label = "Unknown";
          }

          const chipColor = getStatusColor(status);

          return (
            <Chip
              size="small"
              color={chipColor}
              label={label}
              variant="outlined"
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 200,
        sortable: false,
        renderCell: (params) => {
          // Only show accept/reject for unconfirmed bookings
          if (
            params.row.requiresConfirmation &&
            params.row.status === "pending"
          ) {
            return (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={loadingActions[params.row.id] === "accept"}
                  onClick={() => handleAcceptReject(params.row.id, "accept")}
                  startIcon={
                    loadingActions[params.row.id] === "accept" ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CheckIcon />
                    )
                  }
                >
                  {loadingActions[params.row.id] === "accept"
                    ? "Accepting"
                    : "Accept"}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={loadingActions[params.row.id] === "reject"}
                  onClick={() => handleAcceptReject(params.row.id, "reject")}
                  startIcon={
                    loadingActions[params.row.id] === "reject" ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CloseIcon />
                    )
                  }
                >
                  {loadingActions[params.row.id] === "reject"
                    ? "Rejecting"
                    : "Reject"}
                </Button>
              </Box>
            );
          }

          // For other bookings, show action buttons
          return (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  color="default"
                  onClick={() => {
                    setSelectedBookingId(params.row.id);
                    setCancelDialog(true);
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reschedule">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    // Call the handler directly with the booking ID
                    // This ensures availability data is fetched properly
                    handleRescheduleBooking(params.row.id);
                  }}
                >
                  <ScheduleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Request Reschedule">
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => {
                    setSelectedBookingId(params.row.id);
                    setRequestRescheduleDialog(true);
                  }}
                >
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {params.row.externalMeetingId && (
                <Tooltip title="Meeting Summary">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onMeetingSummaryClick(params.row)}
                  >
                    <SummarizeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {params.row.meetingInfo?.link && params.row.meetingInfo?.status !== "revoked" && params.row.meetingInfo?.status !== "expired" && (
                <Tooltip title="Kill Meeting Link">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setSelectedBookingId(params.row.id);
                      setKillLinkDialog(true);
                    }}
                  >
                    <LinkOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [
      loadingActions,
      handleAcceptReject,
      handleRescheduleBooking,
      setSelectedBookingId,
      setCancelDialog,
      setRescheduleDialog,
      setRequestRescheduleDialog,
      setRescheduleDate,
      setRescheduleTime,
      setRescheduleTimezone,
      onMeetingSummaryClick,
      setKillLinkDialog,
    ]
  );

  // Search fields for filtering
  const searchFields = ["name", "email", "eventTitle", "formattedDate"];

  // Custom actions for bulk operations - only show when items are selected
  const customActions =
    selected.length > 0
      ? [
          <Tooltip key="cancel" title="Cancel Selected Bookings">
            <IconButton
              aria-label="Cancel"
              size="large"
              onClick={handleCancelBooking}
              sx={{ mr: 1 }}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>,
          <Tooltip key="reschedule" title="Reschedule Selected Booking">
            <IconButton
              aria-label="Reschedule"
              size="large"
              onClick={handleRescheduleBooking}
              sx={{ mr: 1 }}
            >
              <ScheduleIcon />
            </IconButton>
          </Tooltip>,
          <Tooltip key="request-reschedule" title="Request Reschedule">
            <IconButton
              aria-label="Request Reschedule"
              size="large"
              onClick={handleRequestReschedule}
              sx={{ mr: 1 }}
            >
              <EmailIcon />
            </IconButton>
          </Tooltip>,
          <Tooltip key="delete" title="Delete Selected Booking">
            <IconButton
              aria-label="Delete"
              size="large"
              onClick={handleDeleteBooking}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>,
        ]
      : [];

  return (
    <ReusableTable
      data={processedBookings}
      columns={columns}
      searchQuery=""
      onSearchChange={() => {}} // Search is handled in parent component
      searchPlaceholder="Search bookings..."
      searchFields={searchFields}
      selected={selected}
      onSelectAllClick={handleSelectAllClick}
      onSelect={handleClick}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      viewStyle={viewStyle}
      setViewStyle={setViewStyle}
      hasCardView={true}
      CardComponent={BookingCard}
      cardProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
      onBulkEdit={() => {}} // Not used in bookings
      customActions={customActions}
      title=""
      showSearch={false}
      loading={loading}
      emptyStateMessage="No bookings found"
    />
  );
}

export default BookingsTable;
